// Phase 0085 — Task Verification Screen.
// Lists tasks awaiting verification by the current user; supervisor approves,
// partially approves, or rejects with a quality score.

import { useState } from 'react'
import {
  App, Button, DatePicker, Empty, Form, Input, InputNumber, Modal, Rate,
  Select, Space, Table, Tag, Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs, { type Dayjs } from 'dayjs'
import { useMutation, useQuery } from '@tanstack/react-query'

import { taskApi, type TaskInstance } from '@/features/hr/taskExecutionApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

export default function TaskVerificationPage() {
  const { message } = App.useApp()
  const [onDate, setOnDate] = useState<Dayjs>(dayjs())
  const [verifyOpen, setVerifyOpen] = useState<TaskInstance | null>(null)
  const [form] = Form.useForm()

  const dateStr = onDate.format('YYYY-MM-DD')
  const { data: tasks = [], isLoading, refetch } = useQuery({
    queryKey: ['verify-queue', dateStr],
    queryFn: () => taskApi.verifyQueue(dateStr),
  })

  const verifyMut = useMutation({
    mutationFn: ({ task, values }: { task: TaskInstance; values: { result: string; quality_score?: number; remarks?: string } }) =>
      taskApi.verify(task.id, values),
    onSuccess: () => {
      message.success('Verification recorded')
      setVerifyOpen(null)
      form.resetFields()
      refetch()
    },
    onError: (e: Error) => message.error(e.message),
  })

  const columns: ColumnsType<TaskInstance> = [
    { title: 'Task', dataIndex: 'title' },
    { title: 'Assigned User', dataIndex: 'assigned_to_user_id', width: 200,
      render: (id: string | null) => id ? <Text code style={{ fontSize: 11 }}>{id.substring(0, 8)}…</Text> : '—' },
    {
      title: 'Completed',
      dataIndex: 'completed_at',
      width: 150,
      render: (d: string | null) => d ? dayjs(d).format('MMM D HH:mm') : '—',
    },
    {
      title: 'Evidence',
      dataIndex: 'evidence_url',
      width: 100,
      render: (u: string | null) => u ? <a href={u} target="_blank" rel="noreferrer">View</a> : <Tag>None</Tag>,
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_, t) => (
        <Button size="small" type="primary"
                onClick={() => { setVerifyOpen(t); form.resetFields() }}>
          Verify
        </Button>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Verification Queue"
        subtitle="Tasks completed by your team members, waiting for your verification"
        extra={
          <Space>
            <DatePicker value={onDate} onChange={(d) => d && setOnDate(d)} />
            <Button onClick={() => refetch()}>Refresh</Button>
          </Space>
        }
      />
      {tasks.length === 0 ? (
        <Empty description="Nothing waiting for verification." />
      ) : (
        <Table rowKey="id" columns={columns} dataSource={tasks}
               loading={isLoading} pagination={{ pageSize: 25 }} />
      )}

      <Modal
        open={!!verifyOpen}
        title={`Verify: ${verifyOpen?.title || ''}`}
        onCancel={() => setVerifyOpen(null)}
        onOk={() => form.submit()}
        confirmLoading={verifyMut.isPending}
      >
        <Form form={form} layout="vertical"
              initialValues={{ result: 'APPROVED', quality_score: 90 }}
              onFinish={(values) => verifyOpen && verifyMut.mutate({ task: verifyOpen, values })}>
          <Form.Item name="result" label="Result" rules={[{ required: true }]}>
            <Select options={[
              { value: 'APPROVED', label: 'Approved' },
              { value: 'PARTIAL', label: 'Partial (needs follow-up)' },
              { value: 'REJECTED', label: 'Rejected' },
            ]} />
          </Form.Item>
          <Form.Item name="quality_score" label="Quality Score (0-100)">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remarks" label="Verifier Remarks">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
